import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const Header = ({ user, onLogout, onNavigateToApp }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [favoritesOpen, setFavoritesOpen] = useState(false);
    const [reservations, setReservations] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [loadingFavorites, setLoadingFavorites] = useState(false);

    const handleLogout = () => {
        toast.success('Logged out successfully');
        onLogout();
        setDropdownOpen(false);
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const toggleFavorites = () => {
        setFavoritesOpen(!favoritesOpen);
        if (!favoritesOpen && (reservations.length === 0 || favorites.length === 0)) {
            fetchFavoritesData();
        }
    };

    const fetchFavoritesData = async () => {
        try {
            setLoadingFavorites(true);
            
            // Fetch both reservations and favorites in parallel
            const [reservationsResponse, favoritesResponse] = await Promise.all([
                axios.get(getApiUrl('/api/my-reservations')),
                axios.get(getApiUrl('/api/favorites'))
            ]);
            
            setReservations(reservationsResponse.data.reservations || []);
            setFavorites(favoritesResponse.data.favorites || []);
        } catch (error) {
            console.error('Error fetching favorites data:', error);
            toast.error('Failed to load favorites data');
        } finally {
            setLoadingFavorites(false);
        }
    };

    const fetchReservations = async () => {
        try {
            setLoadingFavorites(true);
            const response = await axios.get(getApiUrl('/api/my-reservations'));
            setReservations(response.data.reservations || []);
        } catch (error) {
            console.error('Error fetching reservations:', error);
            toast.error('Failed to load reserved apps');
        } finally {
            setLoadingFavorites(false);
        }
    };

    const fetchFavorites = async () => {
        try {
            setLoadingFavorites(true);
            const response = await axios.get(getApiUrl('/api/favorites'));
            setFavorites(response.data.favorites || []);
        } catch (error) {
            console.error('Error fetching favorites:', error);
            toast.error('Failed to load favorite apps');
        } finally {
            setLoadingFavorites(false);
        }
    };

    const handleAppClick = (appPath) => {
        onNavigateToApp(appPath);
        setFavoritesOpen(false);
    };

    const handleReleaseApp = async (appPath, reservationId) => {
        try {
            await axios.post(getApiUrl('/api/app-reservations/release'), { appPath });
            toast.success('App released successfully');
            setReservations(prev => prev.filter(res => res.id !== reservationId));
        } catch (error) {
            console.error('Error releasing app:', error);
            toast.error('Failed to release app');
        }
    };

    const handleRemoveFavorite = async (appPath, favoriteId) => {
        try {
            await axios.delete(getApiUrl('/api/favorites/remove'), { data: { appPath } });
            toast.success('App removed from favorites');
            setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
        } catch (error) {
            console.error('Error removing favorite:', error);
            toast.error('Failed to remove favorite');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimeRemaining = (expiresAt) => {
        if (!expiresAt) return 'No expiry';
        
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diff = expiry - now;
        
        if (diff <= 0) return 'Expired';
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else {
            return `${minutes}m`;
        }
    };

    const parseAppPath = (appPath) => {
        const parts = appPath.split('/');
        return {
            typology: parts[0] || 'N/A',
            cluster: parts[1] || 'N/A',
            app: parts[2] || 'N/A'
        };
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownOpen && !event.target.closest('.dropdown')) {
                setDropdownOpen(false);
            }
            if (favoritesOpen && !event.target.closest('.favorites-dropdown')) {
                setFavoritesOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen, favoritesOpen]);

    return (
        <motion.header 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="header-bg text-white py-2 mb-2"
        >
            <div className="d-flex justify-content-between align-items-center px-4">
                <motion.div 
                    className="d-flex align-items-center gap-3"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                >
                    <img 
                        src="/O_plus_delivery_transparent.png" 
                        width="40" 
                        height="40" 
                        alt="Delivery Express Logo"
                        className="rounded-circle shadow-sm"
                    />
                    
                    <div className="text-start">
                        <h4 className="mb-0 fw-bold">
                            Delivery Express
                        </h4>
                        <div className="d-flex align-items-center gap-2">
                            <small className="opacity-75">Enterprise Infrastructure Management</small>
                            <motion.div
                                animate={{ x: [0, 3, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <img src="/rocket.svg" width="12" alt="Rocket" />
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {user && (
                    <motion.div 
                        className="d-flex align-items-center gap-3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        {/* Favorites Section */}
                        <div className="favorites-dropdown position-relative">
                            <motion.button 
                                className="favorites-btn"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={toggleFavorites}
                                type="button"
                            >
                                <i className="bi bi-bookmark-fill"></i>
                                {(reservations.length > 0 || favorites.length > 0) && (
                                    <span className="favorites-badge">
                                        {reservations.length + favorites.length}
                                    </span>
                                )}
                            </motion.button>

                            {favoritesOpen && (
                                <motion.div 
                                    className="favorites-dropdown-menu show"
                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <div className="favorites-header">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h6 className="mb-0 fw-bold text-white">
                                                <i className="bi bi-bookmark-fill me-2"></i>
                                                My Favorites & Reservations
                                            </h6>
                                            <button 
                                                className="btn btn-sm btn-outline-light"
                                                onClick={fetchFavoritesData}
                                                disabled={loadingFavorites}
                                            >
                                                <i className={`bi ${loadingFavorites ? 'bi-arrow-clockwise animate-spin' : 'bi-arrow-clockwise'}`}></i>
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="favorites-content">
                                        {loadingFavorites ? (
                                            <div className="text-center py-3">
                                                <div className="spinner-border spinner-border-sm text-white" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="text-white-50 mt-2 mb-0 small">Loading favorites...</p>
                                            </div>
                                        ) : (favorites.length > 0 || reservations.length > 0) ? (
                                            <div className="favorites-list">
                                                {/* Favorites Section */}
                                                {favorites.length > 0 && (
                                                    <div className="favorites-section">
                                                        <div className="favorites-section-header">
                                                            <h6 className="mb-2 fw-bold text-primary">
                                                                <i className="bi bi-star-fill me-2"></i>
                                                                Favorite Apps ({favorites.length})
                                                            </h6>
                                                        </div>
                                                        {favorites.map((favorite, index) => (
                                                            <motion.div 
                                                                key={favorite.id}
                                                                className="favorite-item favorite-item-star"
                                                                initial={{ opacity: 0, x: -10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                transition={{ delay: 0.1 * index }}
                                                                onClick={() => handleAppClick(favorite.appPath)}
                                                            >
                                                                <div className="favorite-item-content">
                                                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                                                        <h6 className="mb-0 fw-bold text-dark">
                                                                            <i className="bi bi-star-fill me-1 text-warning"></i>
                                                                            {favorite.appName}
                                                                        </h6>
                                                                        <span className="badge bg-primary small">
                                                                            <i className="bi bi-star me-1"></i>
                                                                            Favorite
                                                                        </span>
                                                                    </div>
                                                                    
                                                                    <div className="small text-muted mb-2">
                                                                        <div><strong>Typology:</strong> {favorite.typology}</div>
                                                                        <div><strong>Cluster:</strong> {favorite.cluster}</div>
                                                                        {favorite.notes && (
                                                                            <div><strong>Notes:</strong> {favorite.notes}</div>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    <div className="small text-muted">
                                                                        Favorited: {formatDate(favorite.favoritedAt)}
                                                                    </div>
                                                                </div>
                                                                
                                                                <div className="favorite-item-actions">
                                                                    <button 
                                                                        className="btn btn-primary btn-sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleAppClick(favorite.appPath);
                                                                        }}
                                                                        title="Go to Deployment Page"
                                                                    >
                                                                        <i className="bi bi-arrow-right"></i>
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleRemoveFavorite(favorite.appPath, favorite.id);
                                                                        }}
                                                                        title="Remove from Favorites"
                                                                    >
                                                                        <i className="bi bi-star"></i>
                                                                    </button>
                                                                </div>
                                                            </motion.div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Reservations Section */}
                                                {reservations.length > 0 && (
                                                    <div className="favorites-section">
                                                        <div className="favorites-section-header">
                                                            <h6 className="mb-2 fw-bold text-warning">
                                                                <i className="bi bi-bookmark-fill me-2"></i>
                                                                Reserved Apps ({reservations.length})
                                                            </h6>
                                                        </div>
                                                        {reservations.map((reservation, index) => {
                                                            const appInfo = parseAppPath(reservation.appPath);
                                                            const timeRemaining = getTimeRemaining(reservation.expiresAt);
                                                            
                                                            return (
                                                                <motion.div 
                                                                    key={reservation.id}
                                                                    className="favorite-item favorite-item-reserved"
                                                                    initial={{ opacity: 0, x: -10 }}
                                                                    animate={{ opacity: 1, x: 0 }}
                                                                    transition={{ delay: 0.1 * (favorites.length + index) }}
                                                                    onClick={() => handleAppClick(reservation.appPath)}
                                                                >
                                                                    <div className="favorite-item-content">
                                                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                                                            <h6 className="mb-0 fw-bold text-dark">
                                                                                <i className="bi bi-bookmark-fill me-1 text-warning"></i>
                                                                                {appInfo.app}
                                                                            </h6>
                                                                            <span className="badge bg-warning text-dark small">
                                                                                <i className="bi bi-clock me-1"></i>
                                                                                {timeRemaining}
                                                                            </span>
                                                                        </div>
                                                                        
                                                                        <div className="small text-muted mb-2">
                                                                            <div><strong>Typology:</strong> {appInfo.typology}</div>
                                                                            <div><strong>Cluster:</strong> {appInfo.cluster}</div>
                                                                            {reservation.purpose && (
                                                                                <div><strong>Purpose:</strong> {reservation.purpose}</div>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        <div className="small text-muted">
                                                                            Reserved: {formatDate(reservation.reservedAt)}
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    <div className="favorite-item-actions">
                                                                        <button 
                                                                            className="btn btn-primary btn-sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleAppClick(reservation.appPath);
                                                                            }}
                                                                            title="Go to Deployment Page"
                                                                        >
                                                                            <i className="bi bi-arrow-right"></i>
                                                                        </button>
                                                                        <button 
                                                                            className="btn btn-outline-danger btn-sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleReleaseApp(reservation.appPath, reservation.id);
                                                                            }}
                                                                            title="Release Reservation"
                                                                        >
                                                                            <i className="bi bi-x-circle"></i>
                                                                        </button>
                                                                    </div>
                                                                </motion.div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <i className="bi bi-bookmark display-4 text-white-50"></i>
                                                <p className="text-white-50 mt-2 mb-0">No favorites or reservations</p>
                                                <p className="text-white-50 small">Add apps to favorites or reserve them to see them here</p>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </div>

                        {/* User Account Section */}
                        <div className="user-account-compact">
                            {/* Compact User Info */}
                            <div className="d-flex align-items-center gap-2">
                                <motion.div 
                                    className="compact-avatar"
                                    whileHover={{ scale: 1.1 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <div className="avatar-circle-small">
                                        <i className="bi bi-person-fill"></i>
                                    </div>
                                </motion.div>
                                
                                <div className="d-none d-sm-block">
                                    <div className="compact-user-info">
                                        <span className="compact-name">{user.firstName}</span>
                                        <span className={`compact-role role-${user.role}`}>
                                            <i className={`bi bi-${user.role === 'admin' ? 'shield-fill' : user.role === 'user' ? 'person-fill' : 'eye-fill'}`}></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Compact Account Menu */}
                            <div className="dropdown">
                                <motion.button 
                                    className="compact-menu-btn"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={toggleDropdown}
                                    type="button"
                                >
                                    <i className="bi bi-chevron-down"></i>
                                </motion.button>

                                {dropdownOpen && (
                                    <motion.div 
                                        className="compact-dropdown-menu show"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                    >
                                        <div className="compact-dropdown-header">
                                            <div className="compact-user-details">
                                                <div className="compact-avatar-dropdown">
                                                    <i className="bi bi-person-fill"></i>
                                                </div>
                                                <div>
                                                    <div className="compact-username">{user.username}</div>
                                                    <div className="compact-email">{user.email}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="compact-dropdown-divider"></div>
                                        <button 
                                            className="compact-dropdown-item text-danger"
                                            onClick={handleLogout}
                                        >
                                            <i className="bi bi-box-arrow-right"></i>
                                            <span>Logout</span>
                                        </button>
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </motion.header>
    );
};

export default Header;
